import moment from 'moment';
import { DisplayDecorator } from '../../decorators/display-decorator';
import { ODataObject } from '../../interfaces/odata-model.interface';
import { CustomInjector } from '../../services/custom-injector/custom-injector';
import { ModelOptionsLoaderService } from '../../services/model-options-loader/model-options-loader.service';
import { ValueHelperService } from '../../services/value-helper/value-helper.service';
import { CompositeModel } from '../complex/composite.model';
import { DropdownModel } from '../complex/dropdown.model';
import { DURATION_ODATA_FORMAT, DurationModelOptions } from '../options/duration-model-options';
import { DateModel } from '../primitive/date.model';
import { DurationModel } from '../primitive/duration.model';
import { TextModel } from '../primitive/text.model';
import { TimeModel } from '../primitive/time.model';

const odataWorkCode = 'WorkCode';
const odataStartDay = 'StartDay';
const odataStartTime = 'StartTime';
const odataEndDay = 'EndDay';
const odataEndTime = 'EndTime';
const odataDuration = 'Duration';

export class TimeRecordingEntryP40Model extends CompositeModel {
    protected valueHelperService: ValueHelperService;

    public startTime: TimeModel;
    public startDate: DateModel;
    public duration: DurationModel;
    public category: DropdownModel<TextModel>;
    public delivery: DropdownModel<TextModel>;
    public bookingCategory: DropdownModel<TextModel>;
    public comments: TextModel;
    public deliveryType: string;
    public division: string;
    public constructor(
        deliveryType: string,
        division: string,
        startDate = moment.invalid(),
        duration?: moment.Duration,
    ) {
        super('TimeRecordingP40Model');
        const injector = CustomInjector.getInjector();
        this.valueHelperService = injector.get(ValueHelperService);
        const modelOptionsLoader = injector.get(ModelOptionsLoaderService);

        this.startTime = <TimeModel>this.set(new TimeModel(odataStartTime, startDate.clone()));
        this.startDate = <DateModel>this.set(new DateModel(odataStartDay, startDate.clone()));
        modelOptionsLoader.load(this.startDate, 'TimeRecordingP40', 'StartDate');

        // GW is expecting as a value a number for the duration field.
        // The value of the duration field should be sent as a string and not as a number.
        this.duration = <DurationModel>this.set(
            new DurationModel(
                odataDuration,
                duration,
                new DurationModelOptions({
                    format: DURATION_ODATA_FORMAT.MINUTES,
                    step: 15,
                    maxValue: moment.duration({
                        hours: 24,
                    }),
                }),
            ),
        );
        modelOptionsLoader.load(this.duration, 'TimeRecordingP40', 'Duration');

        const categoryValues = this.valueHelperService.getTimeRecordingCategoryValues();
        const categoriesEntries = categoryValues.map(
            (vh) => new DisplayDecorator<TextModel>(vh.descText, new TextModel('Category', vh.code)),
        );

        this.category = new DropdownModel<TextModel>('Category', categoriesEntries);
        modelOptionsLoader.load(this.category, 'TimeRecordingP40', 'Category');

        const deliveryValues = this.valueHelperService.getTimeRecordingDeliveryValues(deliveryType);
        const deliveryEntries = deliveryValues.map(
            (vh) => new DisplayDecorator<TextModel>(vh.descText, new TextModel('Delivery', vh.code)),
        );

        this.delivery = new DropdownModel<TextModel>('Delivery', deliveryEntries);
        modelOptionsLoader.load(this.delivery, 'TimeRecordingP40', 'Delivery');

        const bookingCategoryValues = this.valueHelperService.getTimeRecordingBookingCategoryValues(deliveryType);
        const bookingCategoryEntries = bookingCategoryValues.map(
            (vh) => new DisplayDecorator<TextModel>(vh.descText, new TextModel('BookingCategory', vh.code)),
        );
        this.bookingCategory = new DropdownModel<TextModel>('BookingCategory', bookingCategoryEntries);
        modelOptionsLoader.load(this.bookingCategory, 'TimeRecordingP40', 'BookingCategory');

        this.comments = <TextModel>this.set(new TextModel('Comment'));
        modelOptionsLoader.load(this.comments, 'TimeRecordingP40', 'Comments');

        this.deliveryType = deliveryType;
        this.division = division;
    }

    public getOutModel(): ODataTimeRecordingP40OutModel {
        // startTime is being initialized with a moment.invalid() - this would cause sending null to the backend.
        // we need to set startTime always to the time of the startDate
        this.startTime.value = this.startDate.value.clone();
        const outModel = <ODataTimeRecordingP40OutModel>super.getOutModel();

        const category = this.category.selectedEntry ? this.category.selectedEntry.value : '';
        const delivery = this.delivery.selectedEntry ? this.delivery.selectedEntry.value : '';
        const bookingCategory = this.bookingCategory.selectedEntry ? this.bookingCategory.selectedEntry.value : '';

        const activityType = this.valueHelperService.getTimeRecordingActivityType(this.deliveryType);
        // Inside the work code for PACE US, following logic is expected:
        // 1. First character will be the activity type based on the delivery Type.
        // 2. Second character will be the code value of the chosen category
        // 3. Third character will be the code value of the chosen delivery
        // 4. The fourth character will be the code value of the bookingCategory
        // 5. The fifth and sixth character will be the modality(division) of the mainEquipment.
        // All these values need to be concatenated and sent to the GW/Backend as a string.
        const workCode = activityType + category + delivery + bookingCategory + this.division;
        outModel[odataWorkCode] = workCode;

        // End date and time can be defaulted to start date and time, since start time, date + duration are enough to
        // calculate the actual end date and time in the backend
        outModel[odataEndDay] = outModel[odataStartDay];
        outModel[odataEndTime] = outModel[odataStartTime];

        return outModel;
    }

    public toJSON(): unknown {
        return {
            startTime: this.startTime.value,
            startDate: this.startDate.value,
            duration: this.duration.value,
            category: this.category.selectedEntry ? this.category.selectedEntry.displayName : undefined,
            delivery: this.delivery.selectedEntry ? this.delivery.selectedEntry.displayName : undefined,
            bookingCategory: this.bookingCategory.selectedEntry
                ? this.bookingCategory.selectedEntry.displayName
                : undefined,
            comments: this.comments.value,
            deliveryType: this.deliveryType,
            division: this.division,
        };
    }
}

export class TimeRecordingEntryP40ModelFromJson extends TimeRecordingEntryP40Model {
    public constructor(data: JsonTimeRecordingP40Model) {
        super(
            data.deliveryType,
            data.division,
            data.startDate ? moment(data.startDate) : moment.invalid(),
            data.duration ? moment.duration(data.duration) : moment.duration(0),
        );

        if (this.category && data.category) {
            this.category.selectByDisplayName(data.category);
        }

        if (this.delivery && data.delivery) {
            this.delivery.selectByDisplayName(data.delivery);
        }

        if (this.bookingCategory && data.bookingCategory) {
            this.bookingCategory.selectByDisplayName(data.bookingCategory);
        }

        this.comments.value = data.comments;
    }
}

export interface JsonTimeRecordingP40Model {
    startDate: string;
    duration: string;
    category: string;
    delivery: string;
    bookingCategory: string;
    comments: string;
    deliveryType: string;
    division: string;
}

export interface ODataTimeRecordingP40OutModel extends ODataObject {
    StartDay: string;
    StartTime: string;
    EndDay: string;
    EndTime: string;
    WorkCode: string;
    Duration: string;
    Comment: string;
}
